import React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Toolbar,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import BrandLogo from '../brand/BrandLogo';
import { PRODUCT_NAME } from '../../utils/brandCopy';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import type { ThemeModePreference } from '../../theme/tokens';
import { focusRing, scrollToId } from './landingStyles';

const brandLinkSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mr: 'auto',
  textDecoration: 'none',
  color: 'text.primary',
  ...focusRing,
};

const BrandLockup: React.FC = () => (
  <>
    <BrandLogo variant="mark" height={40} />
    <Box
      component="span"
      sx={{
        fontWeight: 800,
        fontSize: { xs: '1.05rem', sm: '1.2rem' },
        letterSpacing: '-0.02em',
      }}
    >
      {PRODUCT_NAME}
    </Box>
  </>
);

const PublicNavbar: React.FC = () => {
  const location = useLocation();
  const { preference, setPreference } = useThemeMode();
  const onLanding = location.pathname === '/login' || location.pathname === '/';

  return (
    <AppBar
      component="header"
      position="sticky"
      elevation={0}
      sx={{ bgcolor: 'background.paper', color: 'text.primary' }}
    >
      <Toolbar sx={{ gap: 1, flexWrap: 'wrap', minHeight: { xs: 64, md: 72 }, py: 1 }}>
        {onLanding ? (
          <Box
            component="a"
            href="#sign-in"
            onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
              event.preventDefault();
              scrollToId('sign-in');
            }}
            sx={brandLinkSx}
          >
            <BrandLockup />
          </Box>
        ) : (
          <Box component={RouterLink} to="/login" sx={brandLinkSx}>
            <BrandLockup />
          </Box>
        )}

        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, alignItems: 'center' }}>
          <Button color="inherit" onClick={() => scrollToId('how-it-works')} sx={focusRing}>
            How it works
          </Button>
          <Button color="inherit" onClick={() => scrollToId('pricing')} sx={focusRing}>
            Pricing
          </Button>
          <Button component={RouterLink} to="/learn" color="inherit" sx={focusRing}>
            Learn
          </Button>
        </Box>

        <Tooltip title="Theme">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={preference}
            onChange={(_, value: ThemeModePreference | null) => {
              if (value) setPreference(value);
            }}
            aria-label="Color theme"
          >
            <ToggleButton value="light" aria-label="Light" sx={focusRing}>
              <LightModeIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="dark" aria-label="Dark" sx={focusRing}>
              <DarkModeIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="system" aria-label="System" sx={focusRing}>
              <SettingsBrightnessIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>

        {onLanding ? (
          <Button
            color="inherit"
            onClick={() => scrollToId('sign-in')}
            sx={{ display: { xs: 'none', sm: 'inline-flex' }, ...focusRing }}
          >
            Sign in
          </Button>
        ) : (
          <Button
            component={RouterLink}
            to="/login"
            color="inherit"
            sx={{ display: { xs: 'none', sm: 'inline-flex' }, ...focusRing }}
          >
            Sign in
          </Button>
        )}
        <Button
          component={RouterLink}
          to="/signup"
          variant="contained"
          color="primary"
          sx={{ px: { xs: 1.25, sm: 2 }, ...focusRing }}
        >
          Start free
        </Button>
      </Toolbar>
    </AppBar>
  );
};

export default PublicNavbar;
